import { withNamespaces } from 'react-i18next';
import {
    Card,
    CardBody,
    CardTitle,
    Col,
    Progress,
    Row,
} from 'reactstrap';

import useTopRelevantProductsByRiskLevel from './hooks/useTopRelevantProductsByRiskLevel';

const formatQuantity = (value) => (
    `${Number(value || 0).toLocaleString()} MT`
);

const formatLandEvaluated = (value) => (
    `${Number(value || 0).toLocaleString()} ha`
);

const TopRelevantProductsByRiskLevelSkeleton = () => (
    <Card className="h-100 mb-0">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '60%', height: '24px' }} />
            <Row className="g-4">
                <Col xs="12" md="6">
                    {[1, 2, 3, 4, 5].map((item) => (
                        <div key={item} className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '24px' }} />
                    ))}
                </Col>
                <Col xs="12" md="6">
                    {[1, 2, 3, 4].map((item) => (
                        <div key={item} className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '40px' }} />
                    ))}
                </Col>
            </Row>
        </CardBody>
    </Card>
);

const ProductRow = ({
    product,
    quantity,
    percentage,
    color,
}) => (
    <div className="d-flex align-items-center gap-2 mb-4">
        <span className="flex-shrink-0" style={{ minWidth: '80px' }}>
            {product}
        </span>

        <Progress
            className="flex-grow-1 mb-0"
            value={percentage}
            barStyle={{ backgroundColor: color }}
        />

        <span className="fw-semibold flex-shrink-0 text-end" style={{ minWidth: '90px' }}>
            {formatQuantity(quantity)}
        </span>
    </div>
);

const RiskSummaryRow = ({
    riskLevel,
    percentage,
    landEvaluated,
    showDivider,
}) => (
    <div className={`d-flex justify-content-between align-items-start gap-3 ${showDivider ? 'border-bottom pb-3 mb-3' : ''}`}>
        <span>
            {riskLevel}
        </span>

        <div className="text-end">
            <div className="fw-semibold text-danger">
                {`${percentage}%`}
            </div>
            <small className="text-muted">
                {formatLandEvaluated(landEvaluated)}
            </small>
        </div>
    </div>
);

const TopRelevantProductsByRiskLevel = ({
    t,
}) => {
    const {
        products,
        riskSummary,
        isLoading,
        isFetching,
        isError,
    } = useTopRelevantProductsByRiskLevel({ t });

    if (isLoading || isFetching) {
        return <TopRelevantProductsByRiskLevelSkeleton />;
    }

    if (isError || !products?.length || !riskSummary?.length) {
        return null;
    }

    return (
        <Card className="h-100 mb-0">
            <CardBody>
                <CardTitle tag="h6" className="mb-4 overview-section-title ">
                    {t('Top 5 Relevant Products by Risk Level')}
                </CardTitle>

                <Row className="g-4">
                    <Col xs="12" md="6">
                        {products.map((item) => (
                            <ProductRow
                                key={item.product}
                                product={item.product}
                                quantity={item.quantity}
                                percentage={item.percentage}
                                color={item.color}
                            />
                        ))}
                    </Col>

                    <Col xs="12" md="6">
                        {riskSummary.map((item, index) => (
                            <RiskSummaryRow
                                key={item.riskLevel}
                                riskLevel={item.riskLevel}
                                percentage={item.percentage}
                                landEvaluated={item.landEvaluated}
                                showDivider={index < riskSummary.length - 1}
                            />
                        ))}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(TopRelevantProductsByRiskLevel);
